/**
 * NetSync - a cola entre o jogo local e a rede.
 *
 * De um lado o jogo (PlayerController, CameraRig, WeaponSystem, Ballistics) que
 * continua exatamente como era no single-player; do outro o NetClient, que so
 * sabe transportar mensagem. Este modulo traduz um no outro, nas duas direcoes,
 * e NAO faz mais nada: nao decide dano, nao decide vida, nao decide pontuacao.
 *
 * SAINDO   estado do jogador a NET.STATE_RATE, disparos e RECLAMACAO de acerto.
 * CHEGANDO snapshot, entradas e saidas, disparo alheio, dano, baixa e respawn.
 *
 * Regra que sustenta o modelo de rede (secao D3 do plano): o cliente resolve o
 * raycast e RECLAMA o acerto; quem calcula dano e a vida e o SERVIDOR. Por isso,
 * ao receber `damage`, o hp e ATRIBUIDO, nunca subtraido - somar ou subtrair por
 * conta propria e o que faz a vida na tela divergir da vida real da partida.
 *
 * Em modo campanha o modulo fica INERTE (early return no update e em todos os
 * ouvintes): o PvE nao pode ganhar trafego de rede.
 */

import * as THREE from 'three';
import { EVENTS } from '../core/EventBus.js';
import { NET } from '../../shared/netconfig.js';
import {
  S2C,
  state as stateMsg,
  shot as shotMsg,
  hit as hitMsg,
  makeFlags
} from '../../shared/protocol.js';

// --- temporarios de modulo (o protocolo pede array de 3; nao alocar por envio)
const _posArr = [0, 0, 0];
const _originArr = [0, 0, 0];
const _dirArr = [0, 0, 0];

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

export class NetSync {
  constructor(ctx) {
    this.ctx = ctx;

    // O jogo pausado (menu, placar) nao pode fazer o servidor achar que o
    // jogador travou: o estado continua saindo enquanto a partida esta ativa.
    this.updateWhilePaused = true;

    /** Enquanto false, este modulo nao toca em rede nenhuma. */
    this.active = false;

    /** Sequencia das mensagens do cliente. Nunca reinicia dentro da conexao. */
    this.seq = 0;

    this.stats = { states: 0, shots: 0, hits: 0 };

    this._offEvents = [];
    this._offNet = [];
    this._bound = false;
    this._stateTimer = 0;

    // Espelho do que os eventos de arma contam. Existe porque `isADS`/`state` do
    // WeaponSystem sao detalhe interno dele: o evento e o contrato.
    this._ads = false;
    this._reloading = false;

    this._localDead = false;
    this._controllerDisabled = false;
    this._warned = new Set();
  }

  async init() {
    const events = this.ctx.events;
    if (events) {
      this._offEvents.push(events.on(EVENTS.WEAPON_FIRE, (p) => this._onWeaponFire(p)));
      this._offEvents.push(events.on(EVENTS.ENTITY_DAMAGED, (p) => this._onEntityDamaged(p)));
      this._offEvents.push(events.on(EVENTS.WEAPON_ADS, (p) => { this._ads = !!p?.active; }));
      this._offEvents.push(events.on(EVENTS.WEAPON_RELOAD_START, () => { this._reloading = true; }));
      this._offEvents.push(events.on(EVENTS.WEAPON_RELOAD_END, () => { this._reloading = false; }));
    }
    // O NetClient pode ainda nao existir na ordem de boot: a assinatura das
    // mensagens e tentada de novo em setActive() e no update.
    this._bindNet();
  }

  // =========================================================================
  // liga/desliga
  // =========================================================================

  /** Ligado pela MatchState quando a partida online comeca (e desligado no fim). */
  setActive(on) {
    const want = !!on;
    if (want === this.active) return this.active;

    this.active = want;
    this._stateTimer = 0;

    if (want) {
      this._bindNet();
      this._localDead = false;
      // Garante que o boneco do PROPRIO jogador nunca entre como remoto.
      try { this.ctx.remotePlayers?.setLocalId?.(this._localId()); }
      catch (err) { this._warnOnce('setLocalId', err); }
    } else {
      this._restoreController();
      this._localDead = false;
    }
    return this.active;
  }

  isActive() {
    return this.active;
  }

  // =========================================================================
  // laco - saida de estado
  // =========================================================================

  update(dt) {
    // Modo campanha: inerte. Nem timer, nem rede.
    if (!this.active) return;
    try {
      if (!this._bound) this._bindNet();
      const net = this._net();
      if (!net || net.state !== 'online') return;

      const interval = 1 / NET.STATE_RATE;
      this._stateTimer -= Number.isFinite(dt) && dt > 0 ? dt : 0;
      if (this._stateTimer > 0) return;

      // Frame gigante (aba voltando do background) NAO vira rajada de estados
      // atrasados: perde os que ficaram para tras e manda so o atual.
      this._stateTimer += interval;
      if (this._stateTimer <= 0) this._stateTimer = interval;

      this._sendState(net);
    } catch (err) {
      this._warnOnce('update', err);
    }
  }

  dispose() {
    for (const off of this._offEvents) { try { off?.(); } catch (err) { this._warnOnce('off', err); } }
    this._offEvents.length = 0;
    this._unbindNet();
    this._restoreController();
    this.active = false;
  }

  // =========================================================================
  // saindo
  // =========================================================================

  _sendState(net) {
    const ctx = this.ctx;
    const pos = ctx.controller?.position || ctx.player?.position;
    if (!pos || !Number.isFinite(pos.x)) return;

    _posArr[0] = pos.x;
    _posArr[1] = pos.y;
    _posArr[2] = pos.z;

    const yaw = num(ctx.cameraRig?.getYaw?.(), 0);
    const pitch = num(ctx.cameraRig?.getPitch?.(), 0);

    const c = ctx.controller;
    const w = ctx.weapons;
    const flags = makeFlags({
      crouch: !!c?.crouching,
      ads: this._ads || !!w?.isADS,
      sprint: !!c?.sprinting,
      reload: this._reloading || w?.state === 'reloading',
      dead: ctx.player?.alive === false,
      grounded: c ? c.grounded !== false : true
    });

    if (net.send(stateMsg(++this.seq, _posArr, yaw, pitch, flags, num(w?.index, 0)))) {
      this.stats.states++;
    }
  }

  _onWeaponFire(p) {
    if (!this.active || !p || p.fromPlayer === false) return;
    const net = this._net();
    if (!net) return;
    const origin = p.origin;
    const dir = p.dir;
    if (!origin || !dir || !Number.isFinite(origin.x) || !Number.isFinite(dir.x)) return;

    _originArr[0] = origin.x; _originArr[1] = origin.y; _originArr[2] = origin.z;
    _dirArr[0] = dir.x; _dirArr[1] = dir.y; _dirArr[2] = dir.z;

    if (net.send(shotMsg(++this.seq, this._weaponIdOf(p.weapon), _originArr, _dirArr))) {
      this.stats.shots++;
    }
  }

  /**
   * Acerto em jogador REMOTO reclamado ao servidor. NAO aplica dano - a
   * Ballistics tambem nao aplica, porque a entidade remota nao tem `damage()`.
   * Aqui so se RECLAMA: alvo, regiao, arma e distancia. O servidor confere
   * cadencia, alcance e a posicao que ele conhece do alvo, e decide.
   */
  _onEntityDamaged(p) {
    if (!this.active || !p) return;
    const target = p.entity;
    if (!target || target.isRemote !== true || !Number.isFinite(target.id)) return;
    if (!this._isLocalSource(p.source)) return;

    const net = this._net();
    if (!net) return;

    let dist = Number(p.distance);
    if (!Number.isFinite(dist) || dist < 0) {
      const from = this.ctx.controller?.position || this.ctx.player?.position;
      dist = (from && p.point) ? from.distanceTo(p.point) : 0;
    }

    if (net.send(hitMsg(++this.seq, target.id, p.part || 'chest', this._weaponIdOf(p.weapon), dist))) {
      this.stats.hits++;
    }
  }

  // =========================================================================
  // chegando
  // =========================================================================

  _bindNet() {
    if (this._bound) return false;
    const net = this._net();
    if (!net || typeof net.on !== 'function') return false;

    this._offNet.push(net.on(S2C.SNAPSHOT, (m) => this._onSnapshot(m)));
    this._offNet.push(net.on(S2C.PLAYER_JOIN, (m) => this._onPlayerJoin(m)));
    this._offNet.push(net.on(S2C.PLAYER_LEAVE, (m) => this._onPlayerLeave(m)));
    this._offNet.push(net.on(S2C.SHOT, (m) => this._onRemoteShot(m)));
    this._offNet.push(net.on(S2C.DAMAGE, (m) => this._onDamage(m)));
    this._offNet.push(net.on(S2C.KILL, (m) => this._onKill(m)));
    this._offNet.push(net.on(S2C.RESPAWN, (m) => this._onRespawn(m)));
    this._bound = true;
    return true;
  }

  _unbindNet() {
    for (const off of this._offNet) { try { off?.(); } catch (err) { this._warnOnce('offNet', err); } }
    this._offNet.length = 0;
    this._bound = false;
  }

  _onSnapshot(m) {
    if (!this.active || !m) return;
    try {
      this.ctx.remotePlayers?.applySnapshot?.(m.ts, m.players);
    } catch (err) {
      this._warnOnce('snapshot', err);
    }
  }

  _onPlayerJoin(m) {
    if (!this.active) return;
    const info = m?.player;
    if (!info || !Number.isFinite(info.id)) return;
    const you = this._localId();
    if (you !== null && (info.id | 0) === you) return;
    try {
      this.ctx.remotePlayers?.add?.(info);
    } catch (err) {
      this._warnOnce('playerJoin', err);
    }
  }

  _onPlayerLeave(m) {
    if (!this.active || !m || !Number.isFinite(m.id)) return;
    try {
      this.ctx.remotePlayers?.remove?.(m.id | 0);
    } catch (err) {
      this._warnOnce('playerLeave', err);
    }
  }

  /**
   * Disparo de outro jogador. Vira evento para o audio e o FX reagirem - este
   * modulo nao toca som nem cria tracer.
   *
   * Excecao consciente a regra de zero alocacao (a mesma que o WeaponSystem
   * abre para o proprio tiro): origem e direcao saem como Vector3 NOVOS porque
   * audio e FX guardam o payload alem do frame. Sao dois objetos por TIRO.
   */
  _onRemoteShot(m) {
    if (!this.active || !m) return;
    const by = Number.isFinite(m.by) ? (m.by | 0) : null;
    const you = this._localId();
    if (by !== null && you !== null && by === you) return;   // eco do proprio tiro

    const o = m.o;
    const d = m.d;
    if (!Array.isArray(o) || o.length < 3 || !Array.isArray(d) || d.length < 3) return;

    const origin = new THREE.Vector3(+o[0] || 0, +o[1] || 0, +o[2] || 0);
    const dir = new THREE.Vector3(+d[0] || 0, +d[1] || 0, +d[2] || 0);

    this._emit(EVENTS.REMOTE_SHOT, { by, origin, dir, weapon: m.w || null });

    // E TAMBEM AI_FIRE. Sem isto o tiro do adversario e MUDO e sem clarao: o
    // AudioSystem e o FxSystem escutam AI_FIRE (que os bots emitem), e nenhum
    // dos dois conhece REMOTE_SHOT. Num jogo de tiro, ouvir de onde vem o
    // disparo nao e enfeite - e metade da informacao de combate.
    // O payload segue o contrato de AI_FIRE: { bot, origin, dir }. `bot` vai a
    // entidade remota quando ela existe (os dois ouvintes usam optional
    // chaining), so para o som escolher a familia da arma.
    this._emit(EVENTS.AI_FIRE, {
      bot: this.ctx.remotePlayers?.get?.(by) || null,
      origin,
      dir
    });
  }

  /**
   * Dano no jogador LOCAL. O servidor manda o hp resultante: ATRIBUIR e a unica
   * operacao correta aqui - subtrair localmente dessincroniza a vida na primeira
   * mensagem perdida e o jogador morre "sem motivo" na tela dele.
   */
  _onDamage(m) {
    if (!this.active || !m) return;
    const player = this.ctx.player;
    const hp = Number.isFinite(m.hp) ? Math.max(0, m.hp) : null;
    const amount = Number.isFinite(m.amount) ? m.amount : 0;
    const from = this._positionOf(m.by);

    if (player && hp !== null) player.health = hp;

    this._emit(EVENTS.PLAYER_DAMAGED, {
      amount,
      from,
      health: hp !== null ? hp : num(player?.health, 0),
      part: String(m.part || 'chest')
    });

    if (hp !== null && hp <= 0) this._markLocalDeath(this._entityOf(m.by));
  }

  _onKill(m) {
    if (!this.active || !m) return;
    const you = this._localId();
    const killerId = Number.isFinite(m.killer) ? (m.killer | 0) : null;
    const victimId = Number.isFinite(m.victim) ? (m.victim | 0) : null;
    const headshot = !!m.headshot;

    this._emit(EVENTS.KILLFEED, {
      killer: this._nameSourceOf(killerId),
      victim: this._nameSourceOf(victimId),
      weapon: m.w || null,
      headshot
    });

    // Hitmarker de baixa e feedback de quem matou, e so dele.
    if (killerId !== null && you !== null && killerId === you && victimId !== killerId) {
      this._emit(EVENTS.HITMARKER, { headshot, killed: true });
    }

    if (victimId === null) return;
    if (you !== null && victimId === you) {
      this._markLocalDeath(this._entityOf(killerId));
    } else {
      try {
        this.ctx.remotePlayers?.kill?.(victimId, killerId, headshot);
      } catch (err) {
        this._warnOnce('kill', err);
      }
    }
  }

  _onRespawn(m) {
    if (!this.active || !m || !Number.isFinite(m.id)) return;
    const id = m.id | 0;
    const you = this._localId();

    if (you === null || id !== you) {
      try {
        this.ctx.remotePlayers?.respawn?.(id, m.p, m.y, m.hp);
      } catch (err) {
        this._warnOnce('respawn', err);
      }
      return;
    }

    // --- respawn do jogador LOCAL -----------------------------------------
    const ctx = this.ctx;
    try {
      const p = m.p;
      if (Array.isArray(p) && p.length >= 3) {
        ctx.controller?.setPosition?.(+p[0] || 0, +p[1] || 0, +p[2] || 0);
      }
      if (Number.isFinite(m.y)) ctx.cameraRig?.setPose?.(null, m.y, 0);

      const player = ctx.player;
      if (player) {
        // reset() religa as hitboxes no registro de alvos e devolve `alive`;
        // o hp final e o que o SERVIDOR mandou, nao o maximo local.
        if (typeof player.reset === 'function') player.reset();
        else player.alive = true;
        if (Number.isFinite(m.hp)) player.health = Math.max(0, m.hp);
      }
      this._restoreController();
      this._localDead = false;
    } catch (err) {
      this._warnOnce('respawnLocal', err);
    }
  }

  // =========================================================================
  // auxiliares
  // =========================================================================

  /**
   * Marca a morte do jogador local UMA vez. Nao decide nada: a morte ja foi
   * decidida pelo servidor. Cadaver nao anda, por isso o controller sai do ar
   * ate o respawn.
   */
  _markLocalDeath(source) {
    if (this._localDead) return;
    this._localDead = true;

    const player = this.ctx.player;
    if (player) {
      player.health = 0;
      player.alive = false;
    }
    const c = this.ctx.controller;
    if (c && c.enabled !== false) {
      c.enabled = false;
      this._controllerDisabled = true;
    }
    this._emit(EVENTS.PLAYER_DIED, { source: source || null });
  }

  /** Devolve o movimento ao jogador (respawn, saida da partida). */
  _restoreController() {
    if (!this._controllerDisabled) return;
    this._controllerDisabled = false;
    const c = this.ctx.controller;
    if (c) c.enabled = true;
  }

  _net() {
    return this.ctx.net || null;
  }

  /** Meu id na partida. A MatchState e a fonte; o NetClient e a reserva. */
  _localId() {
    const fromMatch = this.ctx.match?.you;
    if (Number.isFinite(fromMatch)) return fromMatch | 0;
    const fromNet = this.ctx.net?.playerId;
    return Number.isFinite(fromNet) ? (fromNet | 0) : null;
  }

  _isLocalSource(src) {
    if (!src) return false;
    return src === this.ctx.player || src === this.ctx.controller || src.isPlayer === true;
  }

  _weaponIdOf(weapon) {
    if (typeof weapon === 'string') return weapon;
    const id = weapon?.id;
    if (typeof id === 'string' && id) return id;
    const current = this.ctx.weapons?.current?.id;
    return typeof current === 'string' ? current : '';
  }

  /**
   * Posicao de quem causou algo, CLONADA: o HUD guarda esse vetor para desenhar
   * o indicador de direcao do dano por alguns segundos.
   */
  _positionOf(id) {
    if (!Number.isFinite(id)) return null;
    const key = id | 0;
    const you = this._localId();
    if (you !== null && key === you) {
      const p = this.ctx.controller?.position;
      return p ? p.clone() : null;
    }
    const r = this.ctx.remotePlayers?.get?.(key);
    return r?.position ? r.position.clone() : null;
  }

  /** Entidade correspondente a um id de jogador (para `source` de eventos). */
  _entityOf(id) {
    if (!Number.isFinite(id)) return null;
    const key = id | 0;
    const you = this._localId();
    if (you !== null && key === you) return this.ctx.player || null;
    return this.ctx.remotePlayers?.get?.(key) || null;
  }

  /**
   * O que o killfeed do HUD sabe ler: a entidade (ele tira o `.name` dela) ou,
   * quando o jogador ja saiu da sala, um texto.
   */
  _nameSourceOf(id) {
    if (id === null) return null;
    const entity = this._entityOf(id);
    if (entity) return entity;
    const roster = this.ctx.match?.players?.get?.(id);
    return roster?.nickname ? String(roster.nickname) : `JOGADOR ${id}`;
  }

  _emit(type, payload) {
    try {
      this.ctx.events?.emit?.(type, payload);
    } catch (err) {
      this._warnOnce('emit', err);
    }
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[NetSync] falha em "${key}" - seguindo sem isso:`, err);
  }
}
